import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { tokens } from '../design-system/tokens';
import { useApp } from '../contexts/AppContext';
import apiClient from '../services/unifiedApiClient';
import ChangeProposalCard from './ChangeProposalCard';
import { cleanName } from '../utils/textUtils';
import DiagramRenderer, { extractDiagramBlocks } from './DiagramRenderer';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

/**
 * Preprocess markdown content to handle common LLM output quirks
 */
const preprocessMarkdown = (content) => {
  if (!content) return '';
  return content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\n{3,}/g, '\n\n');
};

/**
 * Avatar component for messages
 */
const Avatar = ({ type, size = 36 }) => {
  const isUser = type === 'user';
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isUser ? T.orange : T.paperDeep,
      color: isUser ? '#fff' : T.inkMuted,
      fontSize: size * 0.4,
      fontWeight: 700,
      fontFamily: T.hand,
      flexShrink: 0,
      border: isUser ? 'none' : `1px solid ${T.rule}`,
      boxShadow: T.sh1,
    }}>
      {isUser ? 'You' : 'AI'}
    </div>
  );
};

/**
 * Typing indicator component
 */
const TypingIndicator = () => (
  <div style={styles.typingIndicator}>
    <span style={{ ...styles.typingDot, animationDelay: '0ms' }} />
    <span style={{ ...styles.typingDot, animationDelay: '150ms' }} />
    <span style={{ ...styles.typingDot, animationDelay: '300ms' }} />
  </div>
);

/**
 * PlanChatbot - AI-powered idea assistant with full context (spec, features, user journeys, all plan details).
 * Always idea-scoped; conversations are stored on the idea.
 */
const PlanChatbot = ({ ideaId, task, taskTree, onPlanUpdate, idea, embedded = false }) => {
  const { showError, showSuccess } = useApp();


  // Conversation state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingName, setEditingName] = useState('');

  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showConversationMenu, setShowConversationMenu] = useState(false);
  const [hoveredConversation, setHoveredConversation] = useState(null);

  // Proposal state
  const [approvingProposalId, setApprovingProposalId] = useState(null);

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const editInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const menuRef = useRef(null);

  // Abort any in-flight fetch on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Close conversation menu on outside click
  useEffect(() => {
    if (!showConversationMenu) return;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowConversationMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showConversationMenu]);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingConversationId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingConversationId]);

  // Load conversations on mount
  useEffect(() => {
    const loadConversations = async () => {
      try {
        const data = await apiClient.getIdeaConversations(ideaId);
        setConversations(data.conversations || []);

        if (data.active_conversation_id) {
          setActiveConversationId(data.active_conversation_id);
        } else if (data.conversations?.length > 0) {
          setActiveConversationId(data.conversations[0].id);
        }
      } catch (err) {
        console.error('Failed to load conversations:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (ideaId) {
      loadConversations();
    } else {
      setIsLoading(false);
    }
  }, [ideaId]);

  // Load messages when active conversation changes
  useEffect(() => {
    const loadMessages = async () => {
      if (isStreaming) return;

      if (!activeConversationId) {
        setMessages([]);
        return;
      }

      try {
        const data = await apiClient.getIdeaConversation(ideaId, activeConversationId);
        setMessages(data.messages || []);
      } catch (err) {
        console.error('Failed to load messages:', err);
        setMessages([]);
      }
    };

    loadMessages();
  }, [ideaId, activeConversationId]); // Don't include isStreaming - it causes reload after stream ends, erasing proposals

  // Create new conversation
  const createNewConversation = async () => {
    try {
      const data = await apiClient.createIdeaConversation(ideaId);
      const newConversation = data.conversation;
      setConversations(prev => [newConversation, ...prev]);
      setActiveConversationId(newConversation.id);
      setMessages([]);
      showSuccess('New conversation created');
    } catch (err) {
      console.error('Failed to create conversation:', err);
      showError('Failed to create conversation');
    }
  };

  // Rename conversation
  const renameConversation = async (conversationId, newName) => {
    if (!newName.trim()) {
      setEditingConversationId(null);
      return;
    }

    try {
      const data = await apiClient.renameIdeaConversation(ideaId, conversationId, newName.trim());
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, name: data.conversation.name } : c
      ));
      setEditingConversationId(null);
    } catch (err) {
      console.error('Failed to rename conversation:', err);
      showError('Failed to rename conversation');
    }
  };

  // Delete conversation
  const deleteConversation = async (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    const confirmMsg = conversation?.message_count > 0
      ? `Delete "${conversation.name}" with ${conversation.message_count} messages?`
      : `Delete "${conversation?.name}"?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await apiClient.deleteIdeaConversation(ideaId, conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));

      if (conversationId === activeConversationId) {
        const remaining = conversations.filter(c => c.id !== conversationId);
        if (remaining.length > 0) {
          setActiveConversationId(remaining[0].id);
        } else {
          setActiveConversationId(null);
          setMessages([]);
        }
      }
      showSuccess('Conversation deleted');
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      showError('Failed to delete conversation');
    }
  };

  // Start editing conversation name
  const startEditing = (conversation) => {
    setEditingConversationId(conversation.id);
    setEditingName(conversation.name);
  };

  // Handle edit key events
  const handleEditKeyDown = (e, conversationId) => {
    if (e.key === 'Enter') {
      renameConversation(conversationId, editingName);
    } else if (e.key === 'Escape') {
      setEditingConversationId(null);
    }
  };

  // Send message handler
  const sendMessageWithText = async (directMessage = null) => {
    const message = (directMessage || inputValue).trim();

    if (!message || isStreaming) {
      return;
    }

    if (!ideaId) {
      console.error('No ideaId available');
      showError('Chat context not available');
      return;
    }

    setIsStreaming(true);
    setInputValue('');

    let targetConversationId = activeConversationId;
    if (!targetConversationId) {
      try {
        const data = await apiClient.createIdeaConversation(ideaId);
        targetConversationId = data.conversation.id;
        setConversations(prev => [data.conversation, ...prev]);
        setActiveConversationId(targetConversationId);
      } catch (err) {
        console.error('Failed to create conversation:', err);
        showError('Failed to create conversation');
        setIsStreaming(false);
        return;
      }
    }

    const userMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    const assistantId = `assistant_${Date.now()}`;
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    }]);

    try {
      const messagesUrl = apiClient.getIdeaChatMessagesUrl(ideaId, targetConversationId);
      // Create AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const response = await fetch(
        messagesUrl,
        {
          method: 'POST',
          headers: apiClient.getAuthHeaders(),
          body: JSON.stringify({ message }),
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let buffer = '';
      let currentEventType = 'data';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
            continue;
          }

          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEventType === 'token' && data.content) {
                accumulatedContent += data.content;
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantId
                    ? { ...msg, content: accumulatedContent }
                    : msg
                ));
              }

              if (currentEventType === 'proposal' && data.proposal_id && data.changes) {
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantId
                    ? {
                        ...msg,
                        proposals: [
                          ...(msg.proposals || []),
                          {
                            id: data.proposal_id,
                            changes: data.changes,
                            status: 'pending',
                          }
                        ]
                      }
                    : msg
                ));
              }

              if (currentEventType === 'error' || data.error) {
                const errorMsg = data.error || 'Unknown server error';
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantId
                    ? { ...msg, isStreaming: false, error: true, errorDetail: errorMsg, partialContent: accumulatedContent }
                    : msg
                ));
                setIsStreaming(false);
                return;
              }

              currentEventType = 'data';
            } catch (e) {
              // Ignore parsing errors
            }
          }
        }
      }

      setMessages(prev => prev.map(msg =>
        msg.id === assistantId
          ? { ...msg, isStreaming: false }
          : msg
      ));

      setConversations(prev => prev.map(c =>
        c.id === targetConversationId
          ? { ...c, message_count: (c.message_count || 0) + 2 }
          : c
      ));

    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => {
        const existing = prev.find(m => m.id === assistantId);
        return prev.map(msg =>
          msg.id === assistantId
            ? { ...msg, isStreaming: false, error: true, errorDetail: err.message, partialContent: existing?.content || '' }
            : msg
        );
      });
    } finally {
      setIsStreaming(false);
    }
  };

  const sendMessage = () => sendMessageWithText();
  const sendQuickMessage = (message) => sendMessageWithText(message);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Retry: resend with context about what went wrong
  const handleRetry = (assistantMsgId) => {
    const idx = messages.findIndex(m => m.id === assistantMsgId);
    if (idx < 1) return;
    const failedMsg = messages[idx];
    const userMsg = messages[idx - 1];
    if (userMsg?.role !== 'user') return;

    const originalText = userMsg.content;

    // Build retry message with failure context
    let retryMessage = originalText;
    const errorDetail = failedMsg.errorDetail || '';
    const partial = failedMsg.partialContent || '';

    if (errorDetail || partial) {
      const parts = [`[Retrying — previous attempt failed.`];
      if (errorDetail) parts.push(`Error: ${errorDetail}`);
      if (partial) parts.push(`Your partial response was: "${partial.slice(0, 500)}"`);
      parts.push(`Please try again.]\n\n${originalText}`);
      retryMessage = parts.join(' ');
    }

    // Remove the failed pair, then resend
    setMessages(prev => prev.filter(m => m.id !== assistantMsgId && m.id !== userMsg.id));
    sendMessageWithText(retryMessage);
  };

  // Copy assistant message content to clipboard
  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      showSuccess('Copied to clipboard');
    } catch {
      showError('Failed to copy');
    }
  };

  // Handle proposal approval
  const handleApproveProposal = async (proposalId, selectedIndices) => {
    if (!activeConversationId) return;

    setApprovingProposalId(proposalId);

    try {
      const result = await apiClient.approveIdeaProposal(
            ideaId,
            activeConversationId,
            proposalId,
            selectedIndices
          );

      if (result.success) {
        setMessages(prev => prev.map(msg => ({
          ...msg,
          proposals: msg.proposals?.map(p =>
            p.id === proposalId
              ? { ...p, status: 'approved' }
              : p
          )
        })));

        showSuccess(`Applied ${result.applied_count} change${result.applied_count !== 1 ? 's' : ''}`);

        if (onPlanUpdate) {
          onPlanUpdate();
        }
      } else {
        showError('Failed to apply changes');
      }
    } catch (err) {
      console.error('Failed to approve proposal:', err);
      showError(`Failed to apply changes: ${err.message}`);
    } finally {
      setApprovingProposalId(null);
    }
  };

  // Handle proposal rejection
  const handleRejectProposal = async (proposalId) => {
    if (!activeConversationId) return;

    setApprovingProposalId(proposalId);

    try {
      const result = await apiClient.rejectIdeaProposal(
            ideaId,
            activeConversationId,
            proposalId
          );

      if (result.success) {
        setMessages(prev => prev.map(msg => ({
          ...msg,
          proposals: msg.proposals?.map(p =>
            p.id === proposalId
              ? { ...p, status: 'rejected' }
              : p
          )
        })));

        showSuccess('Proposal rejected');
      }
    } catch (err) {
      console.error('Failed to reject proposal:', err);
      showError(`Failed to reject proposal: ${err.message}`);
    } finally {
      setApprovingProposalId(null);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  // Format conversation date
  const formatConversationDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 220px)',
      minHeight: '500px',
      backgroundColor: T.paper,
      borderRadius: T.r4,
      border: `1px solid ${T.rule}`,
      overflow: 'hidden',
      boxShadow: T.sh2,
      fontFamily: T.sans,
      ...(embedded ? { height: '100%', minHeight: 0, borderRadius: 0, border: 'none', boxShadow: 'none' } : {}),
    }}>
      {/* Main Chat Area (full width — no sidebar) */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        backgroundColor: T.paper,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          borderBottom: `1px solid ${T.rule}`,
          backgroundColor: T.paperDeep,
          gap: '8px',
        }}>
          {/* Conversation selector */}
          <div style={{ position: 'relative', flex: 1, minWidth: 0 }} ref={menuRef}>
            <button
              onClick={() => setShowConversationMenu(v => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '6px 12px',
                backgroundColor: 'transparent',
                border: `1px solid ${showConversationMenu ? T.ruleStrong : T.rule}`,
                borderRadius: showConversationMenu ? `${T.r2} ${T.r2} 0 0` : T.r2,
                color: T.ink2,
                fontSize: '13px',
                fontWeight: 600,
                fontFamily: T.sans,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'border-color 0.15s, background-color 0.15s',
                ...(showConversationMenu ? { backgroundColor: `${T.orange}08` } : {}),
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <span style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {conversations.find(c => c.id === activeConversationId)?.name || 'Cahoots AI'}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke={T.inkMuted} strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, transition: 'transform 0.15s', transform: showConversationMenu ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <path d="M2 4l4 4 4-4"/>
              </svg>
            </button>

            {showConversationMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 200,
                backgroundColor: T.paperSoft,
                border: `1px solid ${T.ruleStrong}`,
                borderTop: 'none',
                borderRadius: `0 0 ${T.r2} ${T.r2}`,
                maxHeight: '280px',
                overflowY: 'auto',
                boxShadow: T.sh2,
              }}>
                {isLoading ? (
                  <div style={{ padding: '12px 16px', color: T.inkFaint, fontSize: '13px', fontFamily: T.sans }}>
                    Loading...
                  </div>
                ) : conversations.length === 0 ? (
                  <div style={{ padding: '12px 16px', color: T.inkFaint, fontSize: '13px', fontFamily: T.sans }}>
                    No conversations yet
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s',
                        borderBottom: `1px solid ${T.rule}`,
                        backgroundColor: conv.id === activeConversationId ? `${T.orange}12` : 'transparent',
                      }}
                      onClick={() => {
                        if (!editingConversationId) {
                          setActiveConversationId(conv.id);
                          setShowConversationMenu(false);
                        }
                      }}
                      onMouseEnter={() => setHoveredConversation(conv.id)}
                      onMouseLeave={() => setHoveredConversation(null)}
                    >
                      {editingConversationId === conv.id ? (
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={() => renameConversation(conv.id, editingName)}
                          onKeyDown={(e) => handleEditKeyDown(e, conv.id)}
                          style={{
                            flex: 1,
                            padding: '6px 12px',
                            backgroundColor: T.card,
                            border: `2px solid ${T.orange}`,
                            borderRadius: T.r2,
                            color: T.ink,
                            fontSize: '13px',
                            fontFamily: T.sans,
                            outline: 'none',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <>
                          <span style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: T.ink2,
                            fontSize: '13px',
                            fontFamily: T.sans,
                          }}>{conv.name}</span>
                          <span style={{
                            flexShrink: 0,
                            color: T.inkFaint,
                            fontSize: '11px',
                            whiteSpace: 'nowrap',
                            fontFamily: T.sans,
                          }}>
                            {formatConversationDate(conv.updated_at)}
                          </span>
                          {(hoveredConversation === conv.id || conv.id === activeConversationId) && (
                            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); startEditing(conv); }}
                                style={styles.actionButton}
                                title="Rename"
                              >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill={T.inkMuted}>
                                  <path d="M9.5 1.5l1 1-7 7H2.5v-1l7-7z"/>
                                </svg>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                                style={styles.actionButton}
                                title="Delete"
                              >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill={T.inkMuted}>
                                  <path d="M3 3h6v7H3V3zM5 2h2v1H5V2zM2 3h8v1H2V3z"/>
                                </svg>
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <button
              onClick={createNewConversation}
              disabled={isStreaming}
              title="New conversation"
              style={{
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: `${T.orange}18`,
                color: T.orange,
                border: `1px solid ${T.ruleStrong}`,
                borderRadius: T.r2,
                cursor: 'pointer',
                transition: 'all 0.15s',
                flexShrink: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          backgroundColor: T.paper,
        }}>
          {!activeConversationId && conversations.length === 0 && !isLoading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              padding: '32px',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -60%)',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${T.orange}12 0%, transparent 70%)`,
                pointerEvents: 'none',
              }} />
              <div style={{ fontSize: '48px', marginBottom: '16px', position: 'relative', zIndex: 1 }}>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="20" stroke={T.orange} strokeWidth="2" strokeDasharray="4 4"/>
                  <circle cx="24" cy="24" r="12" fill={`${T.orange}18`}/>
                  <path d="M24 18v4m0 4h.01" stroke={T.orange} strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{
                color: T.ink,
                fontSize: '20px',
                fontWeight: 700,
                fontFamily: T.hand,
                marginBottom: '8px',
                position: 'relative',
                zIndex: 1,
              }}>Cahoots AI</div>
              <div style={{
                color: T.inkMuted,
                fontSize: '13px',
                maxWidth: '400px',
                lineHeight: 1.7,
                marginBottom: '20px',
                position: 'relative',
                zIndex: 1,
                fontFamily: T.sans,
              }}>
                I have full context on this idea — the spec, features, user journeys, and all plan details. Ask me anything.
              </div>
              <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '420px' }}>
                <div style={{
                  color: T.inkFaint,
                  fontSize: '11px',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontWeight: 600,
                  fontFamily: T.sans,
                }}>Try asking</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {[
                    { text: 'What are the riskiest requirements?', icon: '⚠️' },
                    { text: 'What is unclear or underspecified?', icon: '🔍' },
                    { text: 'What should we build first?', icon: '⚡' },
                    { text: 'Draft a technical handoff document', icon: '📄' },
                  ].map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        color: T.ink2,
                        fontSize: '12px',
                        padding: '10px 12px',
                        backgroundColor: T.card,
                        border: `1px solid ${T.rule}`,
                        borderRadius: T.r3,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'left',
                        lineHeight: 1.4,
                        fontFamily: T.sans,
                        boxShadow: T.sh1,
                      }}
                      onClick={() => sendQuickMessage(suggestion.text)}
                      disabled={isStreaming}
                    >
                      <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>{suggestion.icon}</span>
                      <span>{suggestion.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : messages.length === 0 && activeConversationId ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              padding: '32px',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
              <div style={{ color: T.ink, fontSize: '20px', fontWeight: 700, fontFamily: T.hand, marginBottom: '8px' }}>
                Start the conversation
              </div>
              <div style={{ color: T.inkMuted, fontSize: '13px', maxWidth: '400px', lineHeight: 1.7, fontFamily: T.sans }}>
                Ask me about your plan. I can help explain features, generate documentation,
                or plan your sprints.
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    maxWidth: '85%',
                    ...(msg.role === 'user'
                      ? { alignSelf: 'flex-end', flexDirection: 'row-reverse' }
                      : { alignSelf: 'flex-start' }),
                  }}
                >
                  <Avatar type={msg.role} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                    }}>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: T.inkFaint,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontFamily: T.sans,
                      }}>
                        {msg.role === 'user' ? 'You' : 'Cahoots AI'}
                      </span>
                      <span style={{ fontSize: '11px', color: T.inkFaint, opacity: 0.7, fontFamily: T.sans }}>
                        {formatTime(msg.timestamp)}
                      </span>
                    </div>
                    <div style={{
                      padding: '10px 14px',
                      borderRadius: T.r3,
                      fontSize: '14px',
                      lineHeight: 1.6,
                      fontFamily: T.sans,
                      ...(msg.role === 'user'
                        ? {
                            backgroundColor: `${T.orange}18`,
                            border: `1px solid ${T.rule}`,
                            color: T.ink,
                            borderBottomRightRadius: T.r2,
                          }
                        : {
                            backgroundColor: T.card,
                            border: `1px solid ${T.rule}`,
                            color: T.ink,
                            borderBottomLeftRadius: T.r2,
                            boxShadow: T.sh1,
                          }),
                    }}>
                      {msg.role === 'assistant' ? (
                        <>
                          {(() => {
                            const { diagrams, cleanContent } = extractDiagramBlocks(msg.content || '');
                            const processedContent = preprocessMarkdown(cleanContent) || (msg.isStreaming ? '' : '');

                            return (
                              <>
                                {processedContent && (
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      h1: ({node, ...props}) => <h1 style={markdownStyles.h1} {...props} />,
                                      h2: ({node, ...props}) => <h2 style={markdownStyles.h2} {...props} />,
                                      h3: ({node, ...props}) => <h3 style={markdownStyles.h3} {...props} />,
                                      p: ({node, ...props}) => <p style={markdownStyles.p} {...props} />,
                                      ul: ({node, ...props}) => <ul style={markdownStyles.ul} {...props} />,
                                      ol: ({node, ...props}) => <ol style={markdownStyles.ol} {...props} />,
                                      li: ({node, ...props}) => <li style={markdownStyles.li} {...props} />,
                                      code: ({node, inline, ...props}) =>
                                        inline
                                          ? <code style={markdownStyles.inlineCode} {...props} />
                                          : <code style={markdownStyles.codeBlock} {...props} />,
                                      pre: ({node, ...props}) => <pre style={markdownStyles.pre} {...props} />,
                                      blockquote: ({node, ...props}) => <blockquote style={markdownStyles.blockquote} {...props} />,
                                      table: ({node, ...props}) => <table style={markdownStyles.table} {...props} />,
                                      th: ({node, ...props}) => <th style={markdownStyles.th} {...props} />,
                                      td: ({node, ...props}) => <td style={markdownStyles.td} {...props} />,
                                      hr: ({node, ...props}) => <hr style={markdownStyles.hr} {...props} />,
                                      a: ({node, ...props}) => <a style={markdownStyles.a} {...props} />,
                                      strong: ({node, ...props}) => <strong style={markdownStyles.strong} {...props} />,
                                    }}
                                  >
                                    {processedContent}
                                  </ReactMarkdown>
                                )}
                                {diagrams.length > 0 && (
                                  <DiagramRenderer content={msg.content} />
                                )}
                              </>
                            );
                          })()}
                          {msg.isStreaming && <TypingIndicator />}
                          {msg.error && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginTop: '8px',
                              padding: '6px 10px',
                              backgroundColor: `${T.capRed}12`,
                              border: `1px solid ${T.capRed}40`,
                              borderRadius: T.r2,
                              color: T.capRed,
                              fontSize: '12px',
                              fontFamily: T.sans,
                            }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                              </svg>
                              <span>Failed{msg.errorDetail ? `: ${msg.errorDetail}` : ''}</span>
                            </div>
                          )}
                          {msg.proposals?.map(proposal => (
                            <ChangeProposalCard
                              key={proposal.id}
                              proposalId={proposal.id}
                              changes={proposal.changes}
                              status={proposal.status}
                              onApprove={handleApproveProposal}
                              onReject={handleRejectProposal}
                              loading={approvingProposalId === proposal.id}
                            />
                          ))}
                          {/* Action buttons - show after streaming completes */}
                          {!msg.isStreaming && (
                            <div style={{
                              display: 'flex',
                              gap: '4px',
                              marginTop: '8px',
                              opacity: 0.5,
                              transition: 'opacity 0.15s',
                            }}>
                              <button
                                title="Copy"
                                style={styles.msgActionBtn}
                                onClick={() => handleCopy(msg.content)}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                              </button>
                              {msg.error && (
                                <button
                                  title="Retry"
                                  style={styles.msgActionBtn}
                                  onClick={() => handleRetry(msg.id)}
                                  disabled={isStreaming}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        msg.content
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div style={{
          padding: '14px 20px',
          borderTop: `1px solid ${T.rule}`,
          backgroundColor: T.paperDeep,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '10px',
            backgroundColor: T.card,
            border: `1px solid ${T.rule}`,
            borderRadius: T.r3,
            padding: '6px',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            boxShadow: T.sh1,
          }}>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything..."
              style={{
                flex: 1,
                padding: '6px 10px',
                backgroundColor: 'transparent',
                border: 'none',
                color: T.ink,
                fontSize: '14px',
                fontFamily: T.sans,
                outline: 'none',
                resize: 'none',
                minHeight: '24px',
                maxHeight: '150px',
                overflow: 'auto',
                lineHeight: 1.5,
              }}
              disabled={isStreaming}
              rows={1}
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isStreaming}
              style={{
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: (!inputValue.trim() || isStreaming) ? T.paperDeep : T.orange,
                color: (!inputValue.trim() || isStreaming) ? T.inkFaint : '#fff',
                border: `1px solid ${(!inputValue.trim() || isStreaming) ? T.rule : T.orangeDark}`,
                borderRadius: T.r2,
                cursor: (!inputValue.trim() || isStreaming) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                flexShrink: 0,
              }}
              title="Send message"
            >
              {isStreaming ? (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: `2px solid ${T.inkFaint}50`,
                  borderTopColor: T.inkFaint,
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
          <div style={{
            fontSize: '11px',
            color: T.inkFaint,
            textAlign: 'center',
            marginTop: '6px',
            opacity: 0.7,
            fontFamily: T.sans,
          }}>
            Press Enter to send, Shift+Enter for new line
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  actionButton: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    color: T.inkMuted,
    border: 'none',
    borderRadius: T.r2,
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.15s',
  },
  typingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 0',
  },
  typingDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: T.orange,
    animation: 'typing 1s ease-in-out infinite',
  },
  msgActionBtn: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    color: T.inkMuted,
    border: `1px solid ${T.rule}`,
    borderRadius: T.r2,
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.15s',
  },
};

// Markdown-specific styles
const markdownStyles = {
  h1: {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginTop: '16px',
    marginBottom: '10px',
    color: T.ink,
    borderBottom: `1px solid ${T.rule}`,
    paddingBottom: '8px',
    fontFamily: T.hand,
  },
  h2: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: '16px',
    marginBottom: '8px',
    color: T.ink,
    fontFamily: T.hand,
  },
  h3: {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginTop: '12px',
    marginBottom: '8px',
    color: T.ink2,
    fontFamily: T.hand,
  },
  p: {
    marginBottom: '10px',
    lineHeight: 1.7,
    color: T.ink,
  },
  ul: {
    marginBottom: '10px',
    paddingLeft: '20px',
    listStyleType: 'disc',
  },
  ol: {
    marginBottom: '10px',
    paddingLeft: '20px',
    listStyleType: 'decimal',
  },
  li: {
    marginBottom: '4px',
    lineHeight: 1.6,
    color: T.ink,
  },
  inlineCode: {
    backgroundColor: T.paperDeep,
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: T.mono,
    fontSize: '0.9em',
    color: T.orangeDark,
    border: `1px solid ${T.rule}`,
  },
  codeBlock: {
    display: 'block',
    fontFamily: T.mono,
    fontSize: '0.85em',
    lineHeight: 1.5,
    color: T.ink2,
  },
  pre: {
    backgroundColor: T.paperDeep,
    padding: '16px',
    borderRadius: T.r3,
    overflowX: 'auto',
    marginBottom: '10px',
    border: `1px solid ${T.rule}`,
  },
  blockquote: {
    borderLeft: `3px solid ${T.ruleStrong}`,
    paddingLeft: '16px',
    marginLeft: 0,
    marginBottom: '10px',
    color: T.inkMuted,
    fontStyle: 'italic',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '16px',
    fontSize: '0.9em',
  },
  th: {
    backgroundColor: T.paperDeep,
    padding: '8px 12px',
    textAlign: 'left',
    fontWeight: 600,
    borderBottom: `2px solid ${T.ruleStrong}`,
    color: T.ink2,
    fontFamily: T.sans,
  },
  td: {
    padding: '8px 12px',
    borderBottom: `1px solid ${T.rule}`,
    verticalAlign: 'top',
    color: T.ink,
  },
  hr: {
    border: 'none',
    borderTop: `1px solid ${T.rule}`,
    marginTop: '16px',
    marginBottom: '16px',
  },
  a: {
    color: T.orange,
    textDecoration: 'underline',
  },
  strong: {
    fontWeight: 600,
    color: T.ink,
  },
};

// Add keyframe animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.7; }
  }
  @keyframes typing {
    0%, 100% { opacity: 0.3; transform: translateY(0); }
    50% { opacity: 1; transform: translateY(-3px); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
if (typeof document !== 'undefined' && !document.querySelector('style[data-chatbot]')) {
  styleSheet.setAttribute('data-chatbot', 'true');
  document.head.appendChild(styleSheet);
}

export default PlanChatbot;
